import {useCallback, useRef, useState} from 'react'
import {useCLIPromptState} from './use-cli-prompt-state'
import {useEventsPromptState} from './use-events-prompt-state'
import {useEventsSchemaEnabled} from './use-events-schema-enabled'
import {useCreateCommand} from './useCreateCommand'
import {useOptimisticLogs} from '../contexts/OptimisticLogsContext'
import {COMMAND_TYPES} from '../constants/command-types'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'
import {findSessionFanoutTargets} from '../utils/permission-prompt-helpers'
import {SessionState, type CommandType} from '../types/session'

export interface UsePromptResponseSubmissionOptions {
  /** The session whose prompt should be monitored and responded to. */
  sessionId: string | undefined
  /** The task ID for events-based prompt detection (used when copilot_mission_control_session_events_ui is enabled). */
  taskId?: string | undefined
  /** Whether to enable the underlying CLI prompt state hook (typically: !!sessionId
   *  AND the session is in a state that can accept user input). */
  enabled: boolean
  /** Current session state. */
  sessionState?: SessionState
  /** Optional callback invoked when the prompt-response createCommand mutation
   *  fails, after the optimistic record has been rewound. Use this to surface
   *  a user-visible error in the consumer (e.g. setFailedToSubmit). */
  onError?: () => void
}

export interface PromptResponseSubmission {
  /** The current pending prompt, or null when none. */
  pendingPrompt: NormalizedPrompt | null
  /** True while the underlying logs query is loading. */
  isLoading: boolean
  /**
   * True when the pending prompt is older than the resume window AND the
   * session is paused (`waiting_for_user`, i.e. CCA only).
   */
  isExpired: boolean
  /** True while the currently visible prompt has a response command in flight. */
  isPending: boolean
  /** Dismiss the current prompt without sending a response. */
  dismissPrompt: () => void
  /** Submit a response for the pending prompt. No-op when there is no pending
   *  prompt, a previous response is in flight, or sessionId is missing. */
  handlePromptRespond: (toolCallId: string, response: unknown, commandType: CommandType) => void
}

interface PendingPromptResponseSubmission {
  optimisticId: string | null
  resolvedToolCallId: string | null
  sessionFanoutResolvedToolCallIds: string[]
  sessionFanoutOptimisticIds: string[]
}

interface PromptResponseSubmissionSnapshot {
  submissionId: string
}

/**
 * Wires together {@link useCLIPromptState}, {@link useOptimisticLogs}, and a
 * dedicated {@link useCreateCommand} mutation for components that render an
 * interactive prompt (permission_request, ask_user, exit_plan_mode) and submit
 * the user's response.
 *
 * Resolution is optimistic: the prompt is marked resolved synchronously on
 * click, and the rollback in `onError` re-shows it if the underlying
 * createCommand mutation fails.
 */
export function usePromptResponseSubmission({
  sessionId,
  taskId,
  enabled,
  sessionState,
  onError,
}: UsePromptResponseSubmissionOptions): PromptResponseSubmission {
  const useEventsUI = useEventsSchemaEnabled()

  // Logs-based prompt detection (disabled when events UI is active)
  const logsPrompt = useCLIPromptState({
    sessionId,
    enabled: enabled && !useEventsUI,
    sessionState,
  })

  // Events-based prompt detection (disabled when logs UI is active)
  const eventsPrompt = useEventsPromptState({
    taskId,
    sessionId,
    enabled: enabled && useEventsUI,
  })

  const {pendingPrompt, isLoading, isExpired, markPromptResolved, unmarkPromptResolved, dismissPrompt} = useEventsUI
    ? eventsPrompt
    : logsPrompt
  const {addOptimisticResponse, removeOptimisticResponse} = useOptimisticLogs()
  const pendingSubmissionsRef = useRef(new Map<string, PendingPromptResponseSubmission>())
  const pendingSubmissionIdsByPromptIdRef = useRef(new Map<string, string>())
  const [pendingPromptIds, setPendingPromptIds] = useState<Set<string>>(() => new Set())

  const addPendingSubmission = useCallback((submissionId: string, submission: PendingPromptResponseSubmission) => {
    pendingSubmissionsRef.current.set(submissionId, submission)
    const promptIds = getSubmissionPromptIds(submission)
    for (const promptId of promptIds) {
      pendingSubmissionIdsByPromptIdRef.current.set(promptId, submissionId)
    }
    setPendingPromptIds(prev => {
      const next = new Set(prev)
      for (const promptId of promptIds) {
        next.add(promptId)
      }
      return next
    })
  }, [])

  const clearPendingSubmission = useCallback((submissionId: string, submission: PendingPromptResponseSubmission) => {
    pendingSubmissionsRef.current.delete(submissionId)
    const promptIds = getSubmissionPromptIds(submission)
    for (const promptId of promptIds) {
      pendingSubmissionIdsByPromptIdRef.current.delete(promptId)
    }
    setPendingPromptIds(prev => {
      let changed = false
      const next = new Set(prev)
      for (const promptId of promptIds) {
        changed = next.delete(promptId) || changed
      }
      return changed ? next : prev
    })
  }, [])

  const {createCommand} = useCreateCommand({
    onSuccess: (_command, snapshot) => {
      const submissionId = getSubmissionId(snapshot)
      if (!submissionId) return
      const pendingSubmission = pendingSubmissionsRef.current.get(submissionId)
      if (!pendingSubmission) return

      clearPendingSubmission(submissionId, pendingSubmission)
    },
    onError: (_error, snapshot) => {
      const submissionId = getSubmissionId(snapshot)
      if (!submissionId) return
      const pendingSubmission = pendingSubmissionsRef.current.get(submissionId)
      if (!pendingSubmission) return
      if (pendingSubmission.optimisticId) {
        removeOptimisticResponse(pendingSubmission.optimisticId)
      }
      if (pendingSubmission.resolvedToolCallId) {
        unmarkPromptResolved(pendingSubmission.resolvedToolCallId)
      }
      for (const id of pendingSubmission.sessionFanoutOptimisticIds) {
        removeOptimisticResponse(id)
      }
      for (const tcid of pendingSubmission.sessionFanoutResolvedToolCallIds) {
        unmarkPromptResolved(tcid)
      }
      clearPendingSubmission(submissionId, pendingSubmission)
      onError?.()
    },
  })

  const handlePromptRespond = useCallback(
    (toolCallId: string, response: unknown, commandType: CommandType) => {
      if (!pendingPrompt || pendingSubmissionIdsByPromptIdRef.current.has(toolCallId) || !sessionId) return

      // Only surface the "Resuming session…" indicator when the click is
      // recovering a paused (waiting_for_user) session. CLI sessions don't
      // pause, so showing the indicator there is misleading.
      const fromPausedSession = sessionState === SessionState.WaitingForUser

      const optimisticId = addOptimisticResponse({
        toolCallId,
        response,
        commandType,
        fromPausedSession,
      })
      markPromptResolved(toolCallId)
      // Find sibling prompts that the runtime will auto-resolve when
      // we send the triggering prompt's scope=session approval. We
      // optimistically resolve them in the UI (markPromptResolved +
      // addOptimisticResponse) so all prompts hide immediately, but we
      // send only the SINGLE triggering command to the server.
      //
      // Sending a single command (instead of N) avoids the resumed-session
      // N² problem where N concurrent permission_response
      // commands spawned N resume sessions, each independently
      // re-fanning the approval to all N siblings.
      const sessionFanoutTargets =
        commandType === COMMAND_TYPES.PERMISSION_RESPONSE
          ? findSessionFanoutTargets(pendingPrompt, response, logsPrompt.logs, toolCallId)
          : []
      const pendingSubmission: PendingPromptResponseSubmission = {
        optimisticId,
        resolvedToolCallId: toolCallId,
        sessionFanoutResolvedToolCallIds: [],
        sessionFanoutOptimisticIds: [],
      }
      for (const target of sessionFanoutTargets) {
        markPromptResolved(target.toolCallId)
        pendingSubmission.sessionFanoutResolvedToolCallIds.push(target.toolCallId)
        const optId = addOptimisticResponse({
          toolCallId: target.toolCallId,
          response: {...(response as object), promptId: target.toolCallId},
          commandType,
          inferred: true,
          fromPausedSession,
        })
        pendingSubmission.sessionFanoutOptimisticIds.push(optId)
      }
      addPendingSubmission(toolCallId, pendingSubmission)

      // Forward the prompt-specific command type (e.g. PERMISSION_RESPONSE,
      // ASK_USER_RESPONSE) so Mission Control can route the command correctly:
      // CCAv3 steering filters on `command.type !== 'user_message'` to avoid
      // injecting prompt responses as raw user steering messages.
      createCommand({
        sessionId,
        content: JSON.stringify(response),
        type: commandType,
        snapshot: {submissionId: toolCallId} satisfies PromptResponseSubmissionSnapshot,
      })
    },
    [
      pendingPrompt,
      sessionId,
      sessionState,
      createCommand,
      addOptimisticResponse,
      markPromptResolved,
      logsPrompt.logs,
      addPendingSubmission,
    ],
  )

  return {
    pendingPrompt,
    isLoading,
    isExpired,
    isPending: pendingPrompt ? pendingPromptIds.has(pendingPrompt.toolCallId) : false,
    dismissPrompt,
    handlePromptRespond,
  }
}

function getSubmissionPromptIds(submission: PendingPromptResponseSubmission): string[] {
  const promptIds = submission.resolvedToolCallId ? [submission.resolvedToolCallId] : []
  promptIds.push(...submission.sessionFanoutResolvedToolCallIds)
  return promptIds
}

function getSubmissionId(snapshot: unknown): string | undefined {
  if (!snapshot || typeof snapshot !== 'object') return undefined
  const submissionId = (snapshot as PromptResponseSubmissionSnapshot).submissionId
  return typeof submissionId === 'string' ? submissionId : undefined
}
